/**
 * CartDrawer - Slide-out cart panel from the right
 */
import React, { memo, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiMinus, FiPlus, FiTrash2, FiShoppingBag, FiArrowRight, FiBookmark } from 'react-icons/fi';

import styles from './CartDrawer.module.css';
import { useCart, useScrollLock, useBusinessRules } from '@/hooks';
import { getCartItemId } from '@/store/slices/cartSlice';
import { formatCurrency } from '@/utils';
import { ROUTES } from '@/config/routes';
import { slideInFromRight } from '@/config/animations';

const CartDrawer: React.FC = memo(() => {
  const navigate = useNavigate();
  const {
    items,
    savedItems,
    itemCount,
    summary,
    isDrawerOpen,
    removeItem,
    increment,
    decrement,
    closeDrawer,
  } = useCart();

  useScrollLock(isDrawerOpen);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDrawerOpen) {
        closeDrawer();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, closeDrawer]);

  const handleBackdropClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const handleViewCart = useCallback(() => {
    closeDrawer();
    navigate(ROUTES.CART);
  }, [closeDrawer, navigate]);

  const handleCheckout = useCallback(() => {
    closeDrawer();
    navigate(ROUTES.CHECKOUT);
  }, [closeDrawer, navigate]);

  const handleStartShopping = useCallback(() => {
    closeDrawer();
    navigate(ROUTES.PRODUCTS);
  }, [closeDrawer, navigate]);

  const handleProductClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const { freeShippingThreshold } = useBusinessRules();
  const subtotal = summary.subtotal;
  const shippingProgress = Math.min((subtotal / freeShippingThreshold) * 100, 100);
  const isFreeShipping = summary.isFreeShipping;
  const amountToFreeShipping = summary.amountToFreeShipping;

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClick}
          />

          {/* Drawer */}
          <motion.div
            className={styles.drawer}
            variants={slideInFromRight}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>
                Your Cart{' '}
                <span className={styles.itemCountBadge}>
                  ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                </span>
              </h2>
              <button
                className={styles.closeBtn}
                onClick={closeDrawer}
                aria-label="Close cart"
              >
                <FiX />
              </button>
            </div>

            {items.length === 0 ? (
              /* Empty state */
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <FiShoppingBag />
                </div>
                <h3 className={styles.emptyTitle}>Your cart is empty</h3>
                <p className={styles.emptyText}>
                  Discover our dental equipment and supplies.
                </p>
                <button className={styles.shopBtn} onClick={handleStartShopping}>
                  Start Shopping
                  <FiArrowRight />
                </button>
              </div>
            ) : (
              <>
                {/* Free shipping progress */}
                <div className={styles.shippingProgress}>
                  <p className={styles.shippingText}>
                    {isFreeShipping ? (
                      <span className={styles.shippingTextSuccess}>
                        You've unlocked free shipping!
                      </span>
                    ) : (
                      <>
                        Add <span className={styles.shippingTextHighlight}>{formatCurrency(amountToFreeShipping)}</span> more for free shipping
                      </>
                    )}
                  </p>
                  <div className={styles.progressBar}>
                    <div
                      className={`${styles.progressFill} ${isFreeShipping ? styles['progressFill--complete'] : ''}`}
                      style={{ width: `${shippingProgress}%` }}
                    />
                  </div>
                </div>

                {/* Items list */}
                <div className={styles.itemsList}>
                  {items.map(item => (
                    <div key={getCartItemId(item)} className={styles.cartItem}>
                      <Link
                        to={`/products/${item.product.slug}`}
                        className={styles.itemImage}
                        onClick={handleProductClick}
                      >
                        <img
                          src={item.product.thumbnail || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.product.name)}&background=0077B6&color=fff&size=100`}
                          alt={item.product.name}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.product.name)}&background=0077B6&color=fff&size=100`;
                          }}
                        />
                      </Link>
                      <div className={styles.itemContent}>
                        <span className={styles.itemBrand}>{item.product.brand}</span>
                        <Link
                          to={`/products/${item.product.slug}`}
                          className={styles.itemName}
                          onClick={handleProductClick}
                        >
                          {item.product.name}
                        </Link>
                        {item.selectedSize && (
                          <span className={styles.itemSize}>Size: {item.selectedSize}</span>
                        )}
                        <span className={styles.itemPrice}>
                          {formatCurrency(item.product.price * item.quantity)}
                        </span>
                        <div className={styles.itemActions}>
                          <div className={styles.qtyControl}>
                            <button
                              className={styles.qtyBtn}
                              onClick={() => decrement(item.product.id, item.selectedSize)}
                              disabled={item.quantity <= (item.product.moq || 1)}
                              aria-label="Decrease quantity"
                            >
                              <FiMinus />
                            </button>
                            <span className={styles.qtyValue}>{item.quantity}</span>
                            <button
                              className={styles.qtyBtn}
                              onClick={() => increment(item.product.id, item.selectedSize)}
                              aria-label="Increase quantity"
                            >
                              <FiPlus />
                            </button>
                          </div>
                          <button
                            className={styles.removeBtn}
                            onClick={() => removeItem(item.product.id, item.selectedSize)}
                            aria-label="Remove item"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Saved for later link */}
                {savedItems.length > 0 && (
                  <button className={styles.savedLink} onClick={handleViewCart}>
                    <FiBookmark />
                    {savedItems.length} saved for later
                  </button>
                )}

                {/* Footer */}
                <div className={styles.footer}>
                  <div className={styles.subtotalRow}>
                    <span className={styles.subtotalLabel}>Subtotal</span>
                    <span className={styles.subtotalAmount}>{formatCurrency(subtotal)}</span>
                  </div>
                  <p className={styles.footerNote}>Shipping & taxes calculated at checkout</p>
                  <div className={styles.footerActions}>
                    <button className={styles.checkoutBtn} onClick={handleCheckout}>
                      Proceed to Checkout
                      <FiArrowRight />
                    </button>
                    <button className={styles.viewCartBtn} onClick={handleViewCart}>
                      View Full Cart
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

CartDrawer.displayName = 'CartDrawer';

export default CartDrawer;
